import { CONVEYANCE_TYPES, InvalidBasket, MenuGetError, sdkUtils } from '@koala/sdk';
import { createBasketFromOrderId, removeBasketItem, addBasketItem, getMenu } from '@koala/sdk/v4';
import { encode } from 'html-entities';
import Router from 'next/router';
import { type Task } from 'redux-saga';
import {
  all,
  call,
  cancel,
  fork,
  put,
  race,
  type SagaReturnType,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import globalActions from '../global/actions';
import { locationsActions } from '../locations/actions';
import meActions from '../me/actions';
import menuActions from '../menu/actions';
import orderStatusActions from '../orderStatus/actions';
import promoCodeActions from '../promoCode/actions';
import actions from './actions';
import { BASKET_ERROR } from './messages';
import { addProductEventsGoogle } from '@/analytics/commerce/google';
import { PRODUCT_LOCATION_LABELS } from '@/constants/checkout';
import { isReloadRecoverable } from '@/constants/errorSlugCopy';
import { ERROR_MESSAGES } from '@/constants/events';
import { ASAP_VALUE } from '@/constants/global';
import { ROUTES } from '@/constants/routes';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import customizeActions from '@/redux/customize/actions';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { type IBasketFulfillment } from '@/types/fulfillment';
import { type IGlobalToastReopen } from '@/types/global';
import { getOrigin } from '@/utils';
import {
  basketItemOptionSignature,
  buildUnsatisfiedOptionsPrompt,
  findUnsatisfiedBasketLines,
  patchProductOptions,
  prepareExistingOptionsObject,
  removeInvalidItemsFromCart,
  unsatisfiedOptionsPromptKey,
} from '@/utils/basket';
import { isBreakpointSizeMin } from '@/utils/breakpoints';
import { getRequiredOptionGroupName, isRequiredOptionMissingError } from '@/utils/errors';
import {
  prepareErrorMessage,
  sessionStorageBasket,
  sessionStorageOptionPrompts,
} from '@/utils/global';
import { fireGaEvent, gaActions, gaCats } from '@/utils/googleAnalytics';
import { getLocationId } from '@/utils/locations';
import * as ErrorReporter from '@/utils/errorReporter';
import { sentryLogger } from '@/utils/sentryLogger';
import { retryGuardedReadSaga } from '@/utils/withJsonGuard';

/**
 * Initialize and verify sessionStorage baskets to ensure it is in-sync with redux
 * and can work with a fresh menu and a fresh location
 */
function* initializeSessionStorageBasket() {
  const basket = sessionStorageBasket.get();

  if (!basket) {
    yield put(actions.destroyBasket());
  }

  // Handle existing non-stale baskets
  const basketLocationId = basket?.location?.id;
  if (basketLocationId) {
    // @TODO: abstract this into a function
    let wantedAt: string | undefined = undefined;

    if (basket.checkoutBasket?.wanted_at) {
      wantedAt = basket.checkoutBasket.wanted_at;
    } else if (basket?.fulfillment) {
      if (basket.fulfillment.address?.time_wanted) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        wantedAt = basket.fulfillment.address.time_wanted;
      } else if (basket.fulfillment?.time_wanted) {
        wantedAt = basket.fulfillment.time_wanted;
      }
    }

    yield all([
      // 1. Sync the sessionStorage basket with redux to avoid showing an empty state
      put(actions.hydrateBasket()),
      // 2. Sync the sessionStorage menu with the basket menu in redux
      put(menuActions.hydrateBasketMenu()),
    ]);
    yield all([
      // 3. Fetch a fresh menu to refresh the sessionStorage basket and ensure all the items are valid
      //    TODO we could avoid re-fetching the menu if we could verify the sessionStorage menu is fresh
      //    but to do that we need to add a `createdAt` date to the sessionStorage menu first
      put(menuActions.refreshBasketMenu(basketLocationId, wantedAt)),
      // 4. Fetch a fresh location to refresh the sessionStorage basket and ensure our location data is accurate
      put(locationsActions.refreshBasketLocation(basketLocationId)),
    ]);
    // 5. finally, we sync with redux again to reflect the new the menu & location
    yield put(actions.hydrateBasket());

    // Sync conveyance mode with basket fulfillment
    yield put(conveyanceModeActions.syncConveyanceWithBasketFulfillment(basket?.fulfillment));

    // 6. Fire some analytics events
    const fulfillment: IBasketFulfillment = basket.fulfillment;
    if (fulfillment?.type) {
      fireGaEvent(gaCats.global, gaActions.basketFulfillmenSet, {
        label: fulfillment.type,
      });
    }
  }
}

export function* postAddItemSaga(action: ReturnType<typeof actions.addItem>) {
  const localBasket = sessionStorageBasket.get();
  const client = createHttpClient({ origin: getOrigin(window.location.host) });
  /**
   * If a remote basket has been created with the ordering provider,
   * sync the added/modified item with the remote basket.
   */
  const isEditing = typeof action.index === 'number';
  if (localBasket?.checkoutBasket?.id) {
    const { checkoutBasket, content } = localBasket;
    if (isEditing) {
      // Delete the original item first, then add the updated version.
      const editedItemId = content.basket_items[action.index!].id;
      try {
        yield call(
          // @ts-expect-error redux-saga call typing mismatch
          removeBasketItem,
          {
            basketId: checkoutBasket.id,
            locationId: checkoutBasket.location.id,
            itemId: editedItemId,
          },
          { client },
        );

        const res: SagaReturnType<typeof addBasketItem> = yield call(
          addBasketItem,
          {
            basketId: checkoutBasket.id,
            locationId: checkoutBasket.location.id,
            item: patchProductOptions(action.item),
          },
          { client },
        );
        yield all([
          put(actions.success(res)),
          put(actions.basketItemsSyncedWithStore(res.basket_items)),
        ]);
        yield put(orderStatusActions.validateOrder(checkoutBasket.id, checkoutBasket.location.id));
      } catch (error) {
        // Add failed after remove succeeded — re-sync from server to avoid client/server desync.
        ErrorReporter.captureException(error, 'error', {
          eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
          details: { editedItemId, basketId: checkoutBasket.id },
        });
        yield put(orderStatusActions.validateOrder(checkoutBasket.id, checkoutBasket.location.id));
        yield put(
          globalActions.displayErrorToast(
            'There was an error updating your item. Please review your cart.',
            true,
          ),
        );
        return;
      }
    } else {
      /**
       * TEAM-15059: this call used to be unguarded, and the consequences went well past a missing
       * toast. `takeLatest(ADD_ITEM, postAddItemSaga)` forks this worker, `store.ts` registers no
       * `onError`, so a rejection here aborted the fork tree all the way up to the `all([...])` in
       * `redux/sagas.tsx` — killing **every** saga in the app until the diner reloaded the page,
       * having been shown nothing at all. Verified against the real saga: the worker threw with an
       * empty dispatch log, and a sibling watcher stopped responding to its own action afterwards.
       *
       * That is the frontend half of what TEAM-14755 reports as "the diner sees no error message in
       * any field": the envelope was blank, and on this route nothing rendered it regardless.
       *
       * The edit branch above keeps its own catch, and the recovery genuinely differs — though not
       * for the reason its comment used to give. Its `validateOrder` does **not** re-sync the item
       * list: `BASKET_SUCCESS` replaces only `checkoutBasket`, never `content.basket_items`. What it
       * refreshes is totals and validity after a remove that already succeeded server-side. Here
       * nothing reached the server, so a validate would be a pure round-trip whose only possible
       * effect is surfacing an unrelated error on top of the toast we just showed. Hence no
       * `validateOrder` on this path.
       */
      try {
        const res: SagaReturnType<typeof addBasketItem> = yield call(
          addBasketItem,
          {
            basketId: checkoutBasket.id,
            locationId: checkoutBasket.location.id,
            item: patchProductOptions(action.item),
          },
          { client },
        );
        yield all([
          put(actions.success(res)),
          put(actions.basketItemsSyncedWithStore(res.basket_items)),
        ]);
      } catch (error) {
        const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
          prepareErrorMessage,
          BASKET_ERROR.addItemErrorToastSaga,
          error,
        );

        ErrorReporter.captureException(error, 'error', {
          eventName: ERROR_MESSAGES.ADD_ITEM_ERROR,
          details: { basketId: checkoutBasket.id, errorResponse },
        });

        /**
         * The third argument is `displayErrorToast`'s `refresh` flag, which renders a "Refresh Page"
         * button wired to `window.location.reload()` (`components/toast/index.tsx`). Offered only
         * where a reload is genuinely the recovery — today just the stale-menu rejection, whose copy
         * tells the diner to refresh and means it. That copy is coo-api's `user_message` (tier 3 of
         * the render rule), not this repo's: `ERROR_SLUG_COPY` catalogues `option_not_on_menu` as
         * `null` so the backend's sentence renders rather than being shadowed. The button is
         * unaffected by that, because the flag below is derived from the slug and never from the
         * message — which is exactly why the two are read from different places.
         *
         * Not offered for every add-item failure, even though the button looks harmless. A 429 at
         * the edge resolves to "please wait about an hour before trying again" (`prepareErrorMessage`)
         * precisely because retrying extends the lockout — pairing that sentence with a button that
         * reloads and re-fires the request is the one thing the copy is telling the diner not to do.
         *
         * The decision is **not** made here. `isReloadRecoverable` reads the answer off
         * `RELOAD_RECOVERABLE_SLUGS`, declared beside the copy in `@/constants/errorSlugCopy`, so a
         * slug's sentence and its affordance are added in one place and cannot drift apart. This saga
         * used to compare against `ERROR_SLUG.OptionNotOnMenu` inline, which meant a second
         * reload-recoverable slug would have resolved its copy correctly and silently got no button.
         *
         * Read off the raw envelope, not the resolved `message`: matching on copy is the coupling
         * TEAM-13882 removed. `message` is also the only displayable field — `detail` carries the raw
         * upstream text, is reported above, and is never rendered.
         */
        const offerRefresh = isReloadRecoverable(errorResponse.error?.error);

        /**
         * TEAM-15138: coo-api's `required_option_missing` gets a reopen affordance rather than a
         * reload — its `user_message` asks the diner to reopen the item and choose again, and a
         * reload returns them to the same cart with the same unsatisfied group. It is deliberately
         * not a member of `RELOAD_RECOVERABLE_SLUGS` for that reason.
         *
         * The cart line is resolved here rather than from `error_data`: on the add-item route
         * coo-api sends `product_id` and `item_name` but neither an id nor an index, and this saga
         * already knows exactly which item it just tried to add. The reducer's optimistic write
         * appends it (`ADD_ITEM` with no `index`), so it is in the cart — that is the phantom-item
         * gap documented in docs/ADD_TO_CART_FLOW.md, and here it is the thing that makes the
         * affordance possible. Matched by signature rather than assumed to be last, so a
         * concurrent cart change cannot point the button at someone else's item.
         */
        let reopen: IGlobalToastReopen | undefined;
        if (isRequiredOptionMissingError(errorResponse)) {
          const currentItems = sessionStorageBasket.get()?.content?.basket_items ?? [];
          const signature = basketItemOptionSignature(action.item);
          const index = currentItems.map(basketItemOptionSignature).lastIndexOf(signature);
          if (index !== -1) {
            const groupName = getRequiredOptionGroupName(errorResponse);
            reopen = {
              basketItemIndex: index,
              // Same reason as the revalidation prompt's: this toast outlives navigation, so the
              // index is re-verified against the line it described. See `IGlobalToastReopen`.
              expectedSignature: signature,
              ctaLabel: groupName ? `Choose your ${groupName}` : 'Update this item',
            };
          }
        }

        yield put(
          globalActions.displayErrorToast(errorResponse.message, true, offerRefresh, reopen),
        );
        return;
      }
    }
  }
  try {
    // we disable toast notifications for products on mobile devices
    if (isBreakpointSizeMin('medium')) {
      yield put(actions.addItemToast(action.item, action.index ?? undefined));
    }
    yield put(actions.basketItemSuccess(true));
  } catch {}
}

function* reorderDeliveryBasketSaga(action: ReturnType<typeof actions.reorderBasket>) {
  yield takeLatest(
    [globalActions.DELIVERY_ADDRESS_VALID, conveyanceModeActions.CLEAR_DELIVERY_ADDRESS],
    function* () {
      const state: RootState = yield select();
      const updatedFulfillment =
        state.app.conveyanceMode.address || state.app.basket.fulfillment?.address;

      // Wait for the user to click "Continue" before submitting the reorder.
      const { save } = yield race({
        save: take(conveyanceModeActions.SET_DELIVERY_ADDRESS_CONTINUE),
        cancel: take(globalActions.TOGGLE_FULFILLMENT_MODAL),
      });

      if (save) {
        yield put(
          actions.reorderBasketSubmit(
            action.order,
            action.label,
            updatedFulfillment,
            action.routeToCheckout,
            action.skipUnavailable,
          ),
        );
      }
    },
  );
}

export function* reorderBasketSaga(action: ReturnType<typeof actions.reorderBasket>) {
  const state: RootState = yield select();
  try {
    // Determine fulfillment and cue req'd sagas (delivery reordering requires waiting for user input in fulfillment modal)
    if (action.order.order_data.basket.conveyance_type.type === 'delivery') {
      yield put(meActions.toggleReorderReceipt(false));

      yield put(
        globalActions.toggleFulfillmentModal(
          true,
          /** @TODO ensure that `store_location` is non-nullable. */
          // @ts-expect-error
          action.order.store_location,
          CONVEYANCE_TYPES.DELIVERY,
          false,
          null,
          true,
          false,
          true,
        ),
      );

      const deliveryReorderTask: Task = yield fork(reorderDeliveryBasketSaga, action);

      yield take([actions.REORDER_BASKET_SUCCESS, actions.REORDER_BASKET_FAILURE]);
      yield cancel(deliveryReorderTask); // Remove effect on reorder basket success or failure
    } else {
      yield put(
        actions.reorderBasketSubmit(
          action.order,
          action.label,
          /** @TODO allow for a nullable address. */
          // @ts-expect-error
          null,
          action.routeToCheckout,
          action.skipUnavailable,
        ),
      );
    }
  } catch (error) {
    /**
     * TEAM-13882: these used to overwrite `.message` on the SDK's `MenuGetError` / `InvalidBasket`
     * module-level singletons and rely on `prepareErrorMessage` stringifying the result into the
     * displayed copy. Two problems: mutating a shared singleton leaks the last message to every
     * other consumer, and the copy only reached the diner as a side effect of how the funnel built
     * its string. Pass the copy in as the static message instead.
     */
    let staticMessage = 'There was an error reordering this basket.';
    switch (error) {
      case MenuGetError:
        staticMessage =
          'There was an error reordering this basket. Unable to retrieve the menu for this location.';
        break;
      case InvalidBasket:
        staticMessage =
          "We're sorry, none of the items in this purchase are currently available. Redirecting you to the menu.";
        break;
      default:
        break;
    }

    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      staticMessage,
      error,
    );

    yield put(globalActions.displayErrorToast(errorResponse.message, true));
    yield put({ type: actions.REORDER_BASKET_FAILURE });
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.REORDER_ERROR,
      details: errorResponse,
    });

    if (error === InvalidBasket) {
      void Router.push(
        `/store/${getLocationId(action.order.location, state.app.cmsConfig.webConfig)}/${
          action.order.location.label
        }`,
      );
    }
  }
}

/**
 * Replay a past order into a fresh basket — and, since TEAM-15138, tell the diner when the replayed
 * selection no longer satisfies a required option group.
 *
 * Exported for `basket.reorder.saga.test.ts`, which is the only reason anything in this module is
 * exported. This path is the one place the index basis is not simply "the cart" —
 * `filterAvailableItems` drops lines before `INIT_NEW_BASKET` stores them — so the off-by-one it
 * would produce has to be pinned by a test rather than argued for in a comment.
 */
export function* reorderBasketSubmitSaga(action: ReturnType<typeof actions.reorderBasketSubmit>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const pastOrder: SagaReturnType<typeof createBasketFromOrderId> = yield call(
      createBasketFromOrderId,
      {
        orderId: action.order.id,
        locationId: action.order.store_location_id,
        skipUnavailable: action.skipUnavailable,
      },
      { client },
    );
    const [categories]: SagaReturnType<typeof getMenu> = yield* retryGuardedReadSaga(
      () => getMenu({ id: pastOrder.location.id }, { client }),
      {
        route: Router.pathname,
        urlHint: '/menus',
        locationId: pastOrder.location.id?.toString(),
      },
    );

    const availableBasketItems = sdkUtils.filterAvailableItems(
      // @ts-expect-error
      pastOrder,
      categories,
    );

    // If some products are available and some are not, let user know that unavailable products have been removed and continue on
    if (availableBasketItems.length !== pastOrder.basket_items.length) {
      yield put(
        globalActions.displayErrorToast(
          'Some items are not currently available and have been removed from your basket.',
          false,
        ),
      );
    }

    // Initialize basket and fire KA event
    yield put(
      actions.initBasket(
        // @ts-expect-error
        action.order.store_location,
        categories,
        availableBasketItems,
        action?.address,
      ),
    );

    /**
     * TEAM-15138: `filterAvailableItems` above checks that the product still exists and nothing
     * else, so a replayed order can carry a selection that no longer satisfies a required option
     * group — and this path routes straight to checkout, where coo-api rejects the basket with
     * `required_option_missing`. Tell the diner here instead.
     *
     * Indices are positions in `availableBasketItems`, **not** in `pastOrder.basket_items`: the
     * filter drops lines, and `INIT_NEW_BASKET` above stores exactly the array passed to it, so
     * this is the array the reopen affordance will index into.
     *
     * Recorded in `sessionStorageOptionPrompts` so `revalidateBasketItemsSaga`, which runs again on
     * the destination route, does not repeat what was just said.
     */
    const unsatisfiedLines = findUnsatisfiedBasketLines(availableBasketItems, categories);
    const prompt = buildUnsatisfiedOptionsPrompt(unsatisfiedLines, availableBasketItems);
    if (prompt) {
      yield put(globalActions.displayToast(prompt.message, true, false, undefined, prompt.reopen));
    }
    // Outside the branch, matching the other two sites: a reorder with nothing unsatisfied writes
    // `null`, clearing the previous cart's key rather than leaving it for the destination route.
    sessionStorageOptionPrompts.set(
      unsatisfiedOptionsPromptKey(unsatisfiedLines, availableBasketItems),
    );

    yield put(actions.reorderBasketSuccess(action.order, action.routeToCheckout));
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error reordering this basket.',
      error,
    );

    /**
     * TEAM-13882: a NAMED EXCEPTION to "never show `error_description`". This is the only place a
     * diner learns *which* item changed price, so the upstream text is interpolated on purpose — but
     * it is HTML-escaped first, because the global toast renders with `dangerouslySetInnerHTML`. The
     * gate reads the raw `error_description` rather than the resolved message, which no longer
     * contains the upstream wording. A coo-api ask for structured `error_data` (`item_name` /
     * `old_price` / `new_price`) would retire this exception; see docs/DINER_ERROR_MESSAGES.md.
     */
    const priceChangeDetail = errorResponse.error?.error_description;
    const displayMessage = priceChangeDetail?.includes('has changed from')
      ? `Sorry, this basket can’t be reordered because some items have changed. The price of ${encode(
          priceChangeDetail,
        )} Please update your basket and try again.`
      : errorResponse.message;

    if (errorResponse.error?.error === 'could_not_create_basket_items_unavailable') {
      yield put(meActions.toggleReorderReceipt(true));
      yield put(actions.reorderBasketReviewMissingItems());
    } else {
      yield put(meActions.toggleReorderReceipt(false));
      yield put(globalActions.displayErrorToast(displayMessage, true, true));
    }

    yield put({ type: actions.REORDER_BASKET_FAILURE });

    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.REORDER_ERROR,
      details: errorResponse,
    });
  }
}

function* reorderBasketSuccessSaga(action: ReturnType<typeof actions.reorderBasketSuccess>) {
  const state: RootState = yield select();
  // Fire ecommerce add to cart events for each product in the basket resulting from the reorder
  (action.order?.order_data?.basket?.basket_items ?? []).forEach((item) =>
    addProductEventsGoogle(
      //@ts-expect-error TODO: update type of function
      item,
      action.order.location,
      state.app.menu.basketMenu,
      PRODUCT_LOCATION_LABELS.MENU,
    ),
  );

  // If we are required to route the user to checkout (ex: Order History reorder flow), route the user to checkout
  if (action.routeToCheckout) {
    void Router.push(ROUTES.CHECKOUT);
    return;
  }

  // If we are not on the store page, route the user to the store page with an open cart
  if (Router.pathname !== ROUTES.STORE) {
    void Router.push(
      `/store/${getLocationId(
        // @ts-expect-error ensure that `store_location` is non-nullable.
        action.order.store_location,
        state.app.cmsConfig.webConfig,
      )}?openCart=true`,
    );
    return;
  }

  // If we are on the store page, open the cart with no additional routing
  // TODO: I don't like messing with the UI here but I would rather not reload the page in this use case
  yield put(globalActions.toggleBasket(true));
  yield put(meActions.toggleReorderReceipt(false));
}

export function* removeItemSaga(action: ReturnType<typeof actions.removeItem>) {
  const localBasket = sessionStorageBasket.get();
  if (!localBasket) {
    throw new Error('No basket found in session storage');
  }
  const client = createHttpClient({ origin: getOrigin(window.location.host) });
  /**
   * If a remote basket has been created with the ordering provider,
   * sync the added/modified item with the remote basket.
   */
  if (localBasket?.checkoutBasket?.id) {
    const { checkoutBasket, content } = localBasket;
    const basketItemId = action.item.id;

    if (!basketItemId) {
      sentryLogger.info(`removeItemSaga: basket_item_id is missing for index ${action.index}`, {
        actionItem: action.item,
        actionIndex: action.index,
        checkoutBasketBasketItems: checkoutBasket.basket_items,
        contentBasketItems: content.basket_items,
      });
      return;
    }

    const res: SagaReturnType<typeof removeBasketItem> = yield call(
      removeBasketItem,
      {
        basketId: checkoutBasket.id,
        locationId: checkoutBasket.location.id,
        itemId: basketItemId,
      },
      { client },
    );
    yield put(actions.success(res));
  }
}

export function* removeUnavailableBasketItemsSaga(
  action: ReturnType<typeof actions.removeUnavailableBasketItems>,
) {
  const state: RootState = yield select();
  const ids = state.app.basket.unavailableBasketItemIds;
  const checkoutBasket = state.app.basket.checkoutBasket;

  if (!ids.length || !checkoutBasket?.id) {
    return;
  }

  const client = createHttpClient({ origin: getOrigin(window.location.host) });
  const removedIds: string[] = [];
  const failedIds: string[] = [];
  let lastResponse: SagaReturnType<typeof removeBasketItem> | null = null;

  for (const itemId of ids) {
    try {
      const res: SagaReturnType<typeof removeBasketItem> = yield call(
        removeBasketItem,
        {
          basketId: checkoutBasket.id,
          locationId: checkoutBasket.location.id,
          itemId,
        },
        { client },
      );
      removedIds.push(itemId);
      lastResponse = res;
    } catch (err) {
      failedIds.push(itemId);
      sentryLogger.warn('removeUnavailableBasketItemsSaga: failed to remove item', {
        itemId,
        err,
      });
    }
  }

  if (removedIds.length) {
    yield put(actions.removeUnavailableBasketItemsSuccess(removedIds));
  }
  if (lastResponse) {
    yield put(actions.success(lastResponse));
  }
  if (failedIds.length) {
    yield put(
      globalActions.displayToast(
        `${failedIds.length} item${
          failedIds.length === 1 ? '' : 's'
        } could not be removed. Please try again.`,
        true,
      ),
    );
  }

  if (!removedIds.length) {
    return;
  }

  const finalState: RootState = yield select();
  const attemptedWantedAt = finalState.app.conveyanceMode.attemptedWantedAt;

  if (attemptedWantedAt !== null) {
    if (attemptedWantedAt === ASAP_VALUE) {
      yield put(orderStatusActions.wantedAtBasketRemove());
    } else {
      yield put(orderStatusActions.wantedAtBasketSet(attemptedWantedAt));
    }
  }

  if (action.redirectTo) {
    yield call([Router, Router.push], action.redirectTo);
  }
}

/**
 * Copy for the two informational verdicts. Extracted so the precedence rule below reads as a rule
 * rather than as three inlined strings.
 */
const REVALIDATION_COPY = {
  REPRICED:
    'Some items in your cart have changed prices. We have updated your cart to reflect the current prices.',
  REMOVED: 'Some items in your cart are no longer available and have been removed.',
} as const;

/**
 * Re-check a restored cart against a freshly fetched menu — TEAM-15138 rewrote the shape of this.
 *
 * ## What changed and why the structure did too
 *
 * This saga used to be three near-exclusive branches, each re-reading sessionStorage and each
 * dispatching its own `revalidateBasketItemsSuccess` plus its own toast. Adding the option-group
 * check as a fourth would have made that worse, and the old shape already had a latent bug: when
 * price drift **and** a missing product co-occurred, both branches dispatched, and the second
 * (filtered but not repriced) overwrote the first (repriced but not filtered), silently discarding
 * the repricing. One read, one set of verdicts, one dispatch fixes that by construction.
 *
 * The old code re-read sessionStorage immediately before each dispatch, to avoid clobbering an item
 * the diner added between the top of the saga and the dispatch. That concern is met more simply
 * here: the single read below is followed by nothing but synchronous computation, so there is no
 * yield point for a concurrent addition to land in.
 *
 * ## The three verdicts, and which one gets to speak
 *
 * Only one global toast exists, so precedence is explicit rather than emergent from dispatch order:
 *
 * 1. **unsatisfied required options** — the only verdict the diner must act on, and the only one
 *    that blocks checkout. Carries the reopen affordance.
 * 2. **items removed** — informational.
 * 3. **price drift** — informational.
 *
 * When (1) applies, the applicable informational line rides along as `secondaryMessage` rather than
 * being dropped. When it does not, 2-over-3 is what the old code already produced, since the
 * removal toast was dispatched second and overwrote the pricing one.
 */
export function* revalidateBasketItemsSaga(
  action: ReturnType<typeof actions.revalidateBasketItems>,
) {
  const state: RootState = yield select();
  const promoCode = state.app.promoCode;
  const checkoutBasket = state.app.basket.checkoutBasket;
  const appliedPromoCode = promoCode?.applied;

  const localBasket = sessionStorageBasket.get();
  if (!localBasket) {
    return;
  }

  const originalItems = localBasket.content.basket_items;

  // Verdict 1: base-product price drift. `product.cost` is stored already multiplied by quantity
  // (see `editor.tsx`), hence the divide on the way in and the multiply on the way out. Option
  // prices are deliberately not compared here — that has never been part of this check.
  let priceMismatch = false;
  const repricedItems = originalItems.map((item) => {
    const menuProduct = sdkUtils.findMenuItemById(item.product.id, action.menuCategories);
    if (menuProduct && Number(item.product.cost / item.quantity) !== Number(menuProduct.cost)) {
      priceMismatch = true;
      return {
        ...item,
        product: { ...item.product, cost: menuProduct.cost * item.quantity },
      };
    }
    return item;
  });

  // Verdict 2: the product is gone from the menu entirely.
  const survivingItems = removeInvalidItemsFromCart(repricedItems, action.menuCategories);
  const itemsRemoved = survivingItems.length !== repricedItems.length;

  // Verdict 3: the product is still on the menu but its option groups have moved underneath the
  // diner's selection. Run over the survivors only — prompting to re-choose options on an item we
  // are about to remove would be nonsense. Never throws; see the helper.
  const unsatisfiedLines = findUnsatisfiedBasketLines(survivingItems, action.menuCategories);

  const nextBasket =
    priceMismatch || itemsRemoved
      ? {
          ...localBasket,
          content: { ...localBasket.content, basket_items: survivingItems },
        }
      : localBasket;

  yield put(actions.revalidateBasketItemsSuccess(nextBasket));

  const informationalMessage = itemsRemoved
    ? REVALIDATION_COPY.REMOVED
    : priceMismatch
      ? REVALIDATION_COPY.REPRICED
      : undefined;

  // The prompt is keyed on the whole unsatisfied set, not per line — see
  // `unsatisfiedOptionsPromptKey` for why anything per-line silences the second of two identical
  // cart lines forever. Written unconditionally, including the `null` that clears it, so a cart
  // that has come good stops carrying a stale key.
  const promptKey = unsatisfiedOptionsPromptKey(unsatisfiedLines, survivingItems);
  const alreadyPrompted = promptKey !== null && promptKey === sessionStorageOptionPrompts.get();
  const prompt = alreadyPrompted
    ? null
    : buildUnsatisfiedOptionsPrompt(unsatisfiedLines, survivingItems);

  if (prompt) {
    yield put(
      globalActions.displayToast(prompt.message, true, false, informationalMessage, prompt.reopen),
    );
  } else if (informationalMessage) {
    yield put(globalActions.displayToast(informationalMessage, true));
  }

  sessionStorageOptionPrompts.set(promptKey);

  if (checkoutBasket?.promo_codes?.length && appliedPromoCode) {
    yield put(orderStatusActions.promoCodeAdd(appliedPromoCode));
    yield take(orderStatusActions.PROMO_CODE_ADD);
  } else {
    yield put(promoCodeActions.promoCodeError(''));
  }
}

/**
 * Wait for the diner to finish the edit, then re-enter order initialization — TEAM-15138.
 *
 * Only reached from the checkout reconciliation modal. Basket creation failed there, and dismissing
 * that modal to make room for the customizer removes the diner's only re-entry into initialization:
 * every other `initializeOrder` dispatch lives in that modal or on the sidecart's Checkout button,
 * which is a page away. Without this they fix the item and are stranded on a checkout page with
 * nothing to press — a different dead end from the one the new classification replaced.
 *
 * The wait is a race against the diner giving up, not a bare `take`. Closing the customizer without
 * adding changed nothing, so there is nothing to retry — but a worker parked on `ADD_ITEM` alone
 * would stay parked (only another reopen cancels its parent under `takeLatest`) and then fire on
 * whatever the diner added next, re-entering checkout initialization from the menu page they were
 * browsing. `editor.tsx` dispatches `addItem` before `clearProduct`, so a real edit wins the race.
 *
 * ## The residue that race leaves, and why no third signal closes it
 *
 * Closing the customizer is bounded. **Navigating away with it still open is not**: `<Customizer />`
 * is mounted in the app layout, and `SET_PRODUCT_TO_CUSTOMIZE` / `CLEAR_PRODUCT_TO_CUSTOMIZE` are
 * the only two cases in `redux/customize/reducer.ts` that touch its `open` flag — neither is a
 * route action, and `RouteHandler`'s `routeChangeStart` handler, which does close the account
 * dropdown, the fulfillment modal and the reorder receipt, conspicuously skips this one. So a diner
 * who client-side-navigates off `/checkout` carries the modal with them, and this worker stays
 * parked until the next `REOPEN_BASKET_ITEM_FOR_OPTIONS` cancels it under `takeLatest`. If they
 * then satisfy the group, they are pulled into checkout initialization from wherever they now are.
 *
 * Narrow in practice: the modal stays *visibly on screen* across the navigation, so the realistic
 * way out of it is still the close button — the arm already bounded. Two candidate third signals
 * were checked before accepting the residue, and both rejected:
 *
 * - `globalActions.SET_ROUTER_PATH` — dispatched from `routeChangeComplete`, but only
 *   `if (currentPath !== window.location.pathname)`, and `currentPath` is read from a `useSelector`
 *   captured in a handler registered under `[router]`, so that comparison runs against a stale
 *   path. A conditional signal is not a bound; racing against one would read as closed while still
 *   leaking the case it happened to miss.
 * - `showLoading('page-transition')` from `react-redux-loading-bar` — unconditional on
 *   `routeChangeStart`, so it *is* a bound, but taking a third-party loading-bar action type inside
 *   the basket saga couples this recovery to a package chosen for a progress indicator, and its
 *   `hideLoading` partner fires on a 300ms timer.
 *
 * Adding a route watcher purely to close the window would mean a new store subscription and a third
 * action type in one saga's `race`, against a worker parked behind a modal the diner can still see
 * and close. Recorded rather than closed — mirrored in docs/CHECKOUT_PAGE.md.
 *
 * Called with `yield call` from `reopenBasketItemForOptionsSaga`, so its `try`/`catch` covers this
 * too — see there for why that matters in a repo with no saga `onError`.
 *
 * Deliberately **not** exported. `reopenBasketItemForOptionsSaga` is the only caller, and
 * `basket.saga.test.ts` already drives both arms of the race through that saga, so an export would
 * widen this module's surface for nothing — `yarn ts-prune` flagged it `used in module` for exactly
 * that reason. Every other saga exported from this file is exported because a test imports it.
 */
function* resumeCheckoutAfterEditSaga() {
  const { edited }: { edited?: unknown } = yield race({
    edited: take(actions.ADD_ITEM),
    abandoned: take(customizeActions.CLEAR_PRODUCT_TO_CUSTOMIZE),
  });
  if (!edited) {
    return;
  }

  // Read fresh: the edit is exactly what changed the cart, and `initializeOrder` has to be handed
  // the corrected one or it re-sends the basket coo-api just rejected.
  const editedBasket = sessionStorageBasket.get();
  if (!editedBasket?.location?.id) {
    return;
  }
  const fulfillment = editedBasket.fulfillment;
  yield put(
    orderStatusActions.initializeOrder(
      editedBasket.content,
      editedBasket.location.id,
      fulfillment?.time_wanted ?? fulfillment?.address?.time_wanted ?? undefined,
    ),
  );
}

/**
 * Reopen one cart line's customizer so the diner can satisfy a required option group — TEAM-15138.
 *
 * The same three lines the cart's own edit button runs (`components/cart/items.tsx`), lifted into a
 * saga because the surfaces that need it cannot run them: the global toast has no access to
 * `state.app.menu.basketMenu`, and the checkout reconciliation modal would otherwise duplicate the
 * lookup. `<Customizer />` is mounted in the app layout rather than per-page, so this works from
 * checkout as well as from the menu.
 *
 * Reads the cart from sessionStorage rather than the redux mirror for the same reason every other
 * saga here does — sessionStorage is the source of truth and the reducer reconstructs the slice
 * from it.
 *
 * Its own try/catch: this repo registers no saga `onError`, so a throw here would abort the fork
 * tree and take every other saga with it.
 *
 * `resumeCheckout` hands off to `resumeCheckoutAfterEditSaga` above rather than inlining it — the
 * two halves are separate jobs, and the flag is the seam.
 */
export function* reopenBasketItemForOptionsSaga(
  action: ReturnType<typeof actions.reopenBasketItemForOptions>,
) {
  try {
    const state: RootState = yield select();
    const localBasket = sessionStorageBasket.get();
    const item = localBasket?.content?.basket_items?.[action.index];

    if (!item) {
      sentryLogger.warn('Reopen for options: no cart line at that position', {
        eventName: 'REOPEN_BASKET_ITEM_MISS',
        index: action.index,
        basketItemCount: localBasket?.content?.basket_items?.length ?? 0,
      });
      return;
    }

    /**
     * The index was resolved when the affordance was raised, and the toast that carries it is
     * dispatched with `forceDismiss` — which short-circuits the component's 20-second timer, so it
     * survives navigation until the diner acts. `REMOVE_ITEM` splices the cart without touching it,
     * so by click time the position can hold a different line entirely. A bounds check catches the
     * crash, not the mis-target: reopening the wrong item is the failure this ticket exists to stop.
     *
     * Silent rather than toasted, deliberately. The diner asked to fix one thing and the cart has
     * moved on; a second error on top of the one they were already reading explains nothing they
     * can act on, and the next menu load re-prompts with a fresh index anyway.
     */
    if (action.expectedSignature && basketItemOptionSignature(item) !== action.expectedSignature) {
      sentryLogger.warn('Reopen for options: the cart line moved before the diner clicked', {
        eventName: 'REOPEN_BASKET_ITEM_STALE',
        index: action.index,
        expectedSignature: action.expectedSignature,
      });
      return;
    }

    const basketMenu = state.app.menu.basketMenu;
    const product = sdkUtils.findMenuItemById(item.product.id, basketMenu);

    if (!product) {
      // The product is gone from the menu; `removeInvalidItemsFromCart` owns that case and there is
      // nothing to reopen. Deliberately silent to the diner — a toast here would be a second error
      // on top of whichever one produced this click.
      sentryLogger.warn('Reopen for options: product is no longer on the basket menu', {
        eventName: 'REOPEN_BASKET_ITEM_PRODUCT_MISSING',
        productId: item.product.id,
      });
      return;
    }

    yield put(globalActions.dismissToast());
    yield put(
      customizeActions.setProduct({
        label: PRODUCT_LOCATION_LABELS.BASKET,
        product,
        menuCategories: basketMenu,
        item,
        index: action.index,
        existingOptions: prepareExistingOptionsObject(item.options),
      }),
    );

    if (action.resumeCheckout) {
      yield call(resumeCheckoutAfterEditSaga);
    }
  } catch (error) {
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
      details: { index: action.index },
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.INIT_SESSION_STORAGE_BASKET, initializeSessionStorageBasket);
  yield takeLatest(actions.ADD_ITEM, postAddItemSaga);
  yield takeLatest(actions.REMOVE_ITEM, removeItemSaga);
  yield takeLatest(actions.REORDER_BASKET, reorderBasketSaga);
  yield takeLatest(actions.REORDER_BASKET_SUBMIT, reorderBasketSubmitSaga);
  yield takeLatest(actions.REORDER_BASKET_SUCCESS, reorderBasketSuccessSaga);
  yield takeLatest(actions.REVALIDATE_BASKET_ITEMS, revalidateBasketItemsSaga);
  yield takeLatest(actions.REOPEN_BASKET_ITEM_FOR_OPTIONS, reopenBasketItemForOptionsSaga);
  yield takeLatest(actions.REMOVE_UNAVAILABLE_BASKET_ITEMS, removeUnavailableBasketItemsSaga);
}
